import React, { useEffect, useState } from 'react';
import './Gravity4AI.scss';

import heroImage from '../Assets/Images/gravity4ai/g4ai-hero.svg';
import pyramidImage from '../Assets/Images/gravity4ai/g4ai-pyramid.svg';
import bgVector from '../Assets/Images/gravity4ai/g4ai-bg-vector.svg';
import iconPlatformIngestion from '../Assets/Images/gravity4ai/g4ai-icon-platform-ingestion.svg';
import iconPlatformML from '../Assets/Images/gravity4ai/g4ai-icon-platform-ml.svg';
import iconPlatformLLM from '../Assets/Images/gravity4ai/g4ai-icon-platform-llm.svg';
import iconPlatformViz from '../Assets/Images/gravity4ai/g4ai-icon-platform-viz.svg';
import iconETL from '../Assets/Images/gravity4ai/g4ai-icon-etl.svg';
import iconCustomConn from '../Assets/Images/gravity4ai/g4ai-icon-connectors.svg';
import iconZeroCode from '../Assets/Images/gravity4ai/g4ai-icon-zerocode.svg';
import iconCDC from '../Assets/Images/gravity4ai/down.svg';
import iconDataReplication from '../Assets/Images/gravity4ai/g4ai-icon-replication.svg';
import iconIntelligent from '../Assets/Images/gravity4ai/g4ai-icon-intelligent.svg';
import iconGovernance from '../Assets/Images/gravity4ai/g4ai-icon-governance.svg';
import iconAIReady from '../Assets/Images/gravity4ai/g4ai-icon-aiready.svg';
import iconAgent from '../Assets/Images/gravity4ai/g4ai-icon-agent.svg';
import enterpriseBg from '../Assets/Images/gravity4ai/g4ai-enterprise-bg.png';
import enterpriseMobBg from '../Assets/Images/gravity4ai/g4ai-enterprise-mob-bg.png';

const featureCards = [
  { icon: iconETL, title: 'ETL / ELT', subtitle: 'Move data anywhere with ease' },
  { icon: iconCustomConn, title: 'Custom Connectors', subtitle: 'Build the connectors you need, fast' },
  { icon: iconZeroCode, title: 'Zero-Code Pipelines', subtitle: 'Visual pipeline builder' },
  { icon: iconCDC, title: 'Change Data Capture', subtitle: 'React to data changes in real time' },
  { icon: iconDataReplication, title: 'Data Replication Engine', subtitle: 'High performance data sync' },
  { icon: iconIntelligent, title: 'Intelligent Automation', subtitle: 'Adaptive scaling & observability' },
  { icon: iconGovernance, title: 'Data Governance', subtitle: 'Audit, compliance & security' },
  { icon: iconAIReady, title: 'AI-Ready Data', subtitle: 'Power your AI with trusted data' },
  { icon: iconAgent, title: 'Agent Engine', subtitle: 'Connect AI agents to your data' },
];

const platformCards = [
  {
    icon: iconPlatformIngestion,
    title: 'Data Ingestion, Quality & Operations',
    text: 'The foundational layer handles the entire data movement lifecycle from source connectivity through validation, loading, and operational monitoring.',
  },
  {
    icon: iconPlatformML,
    title: 'ML Model Integration',
    text: 'The platform provides native machine learning infrastructure for training, deploying, and monitoring predictive models without requiring a separate MLOps toolchain.',
  },
  {
    icon: iconPlatformLLM,
    title: 'LLM Layer',
    text: 'Seamlessly embed large language models into the core of your operations, whether sourced from multiple cloud providers or deployed on-premise on your own infrastructure.',
  },
  {
    icon: iconPlatformViz,
    title: 'Visualization & Analytics',
    text: "Data is only valuable when it reaches decision-makers in a form they can act on. The platform's analytics layer surfaces insights through multiple interfaces.",
  },
];

const Gravity4AI = () => {
  const [expandedCards, setExpandedCards] = useState<Set<number>>(new Set());
  const [platformInView, setPlatformInView] = useState<Set<number>>(new Set());

  const toggleCard = (i: number) => {
    setExpandedCards(prev => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i); else next.add(i);
      return next;
    });
  };

  useEffect(() => {
    // General animation observer for stat cards, feature cards, etc.
    const animatedElements = document.querySelectorAll('.g4ai-animate:not([data-platform-idx])');
    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('in-view');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15 }
    );
    animatedElements.forEach(el => observer.observe(el));

    // Platform card observer — uses React state to survive re-renders
    const platformObserver = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const idx = parseInt((entry.target as HTMLElement).dataset.platformIdx ?? '');
            if (!isNaN(idx)) setPlatformInView(prev => { const s = new Set(prev); s.add(idx); return s; });
            platformObserver.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15 }
    );
    document.querySelectorAll('[data-platform-idx]').forEach(el => platformObserver.observe(el));

    return () => { observer.disconnect(); platformObserver.disconnect(); };
  }, []);

  return (
    <div className="g4ai-wrapper">

      {/* HERO */}
      <section className="g4ai-hero">
        <div className="g4ai-hero-content">
          <h1 className="g4ai-hero-title">
            Gravity<span className="g4ai-red">4AI</span>
          </h1>
          <p className="g4ai-hero-subtitle">
            Data &amp; AI Platform as a service,<br />
            turns your data into action
          </p>
          <a href="mailto:office@artifit.rs" className="g4ai-btn-primary">
            Get in touch
          </a>
        </div>
        <div className="g4ai-hero-image">
          <img src={heroImage} alt="" />
        </div>
      </section>

      {/* WHY DATA&AI */}
      <section className="g4ai-why">
        <h2 className="g4ai-section-title">
          Why <span className="g4ai-red">Data&amp;AI</span><br />
          matters <span className="g4ai-red">now</span>
        </h2>
        <p className="g4ai-why-text">
          Every AI model is only as reliable as the data it consumes.
          Large language models hallucinate when fed inconsistent inputs.
          Predictive models drift when source data goes stale.
          Real-time decisioning fails when pipelines introduce latency.
        </p>
        <div className="g4ai-stats">
          <div className="g4ai-stat-card g4ai-animate">
            <span className="g4ai-stat-number">92%</span>
            <p>of enterprises plan to increase Data &amp; AI investment over the next 3 years</p>
          </div>
          <div className="g4ai-stat-card g4ai-animate">
            <span className="g4ai-stat-number">Only 24%</span>
            <p>report production-grade capabilities — the rest stall at proof of concept</p>
          </div>
          <div className="g4ai-stat-card g4ai-animate">
            <span className="g4ai-stat-number">#1 Barrier</span>
            <p>Fragmented, ungoverned, inaccessible data</p>
          </div>
        </div>
      </section>

      {/* PLATFORM ARCHITECTURE */}
      <section className="g4ai-platform">
        <img src={bgVector} alt="" className="g4ai-platform-bg" />
        <div className="g4ai-platform-content">
          <h2 className="g4ai-section-title">
            <span className="g4ai-red">Platform</span> Architecture<br />
            Overview
          </h2>
          <p className="g4ai-platform-subtitle">
            The platform is organized around four integrated pillars, each addressing
            a distinct layer of the enterprise data lifecycle:
          </p>

          {/* Solution diagram */}
          <div className="g4ai-solution">
            {/* Triangle top with label centered inside */}
            <div className="g4ai-solution-pyramid">
              <img src={pyramidImage} alt="" />
              <p className="g4ai-solution-label">Solution</p>
            </div>

            {/* 3 pillars */}
            <div className="g4ai-solution-pillars">
              <div className="g4ai-pillar">
                <div className="g4ai-pillar-header">ML Model<br />Integration</div>
                <div className="g4ai-pillar-body">
                  <span>Core logic</span>
                  <span>Feature engineering</span>
                  <span>Training</span>
                  <span>Predicting</span>
                </div>
              </div>
              <div className="g4ai-pillar">
                <div className="g4ai-pillar-header">LLM Layer</div>
                <div className="g4ai-pillar-body">
                  <span>AI Reasoning</span>
                  <span>Natural Query</span>
                  <span>Multi LLM</span>
                </div>
              </div>
              <div className="g4ai-pillar">
                <div className="g4ai-pillar-header">Visualization<br />&amp; Analytics</div>
                <div className="g4ai-pillar-body">
                  <span>Scheduled Reports</span>
                  <span>Dashboard</span>
                  <span>Reports distribution</span>
                </div>
              </div>
            </div>

            {/* Data Ingestion bar */}
            <div className="g4ai-ingestion">
              <div className="g4ai-ingestion-header">
                Data Ingestion, Quality &amp; Operations
              </div>
              <div className="g4ai-ingestion-body">
                <span>Data Connectors</span>
                <span>Data Validation</span>
                <span>Auto Scaling</span>
              </div>
            </div>
          </div>

          <div className="g4ai-platform-cards">
            {platformCards.map((card, i) => (
              <div
                data-platform-idx={i}
                className={`g4ai-platform-card g4ai-animate${platformInView.has(i) ? ' in-view' : ''}${expandedCards.has(i) ? ' g4ai-card-expanded' : ''}`}
                key={i}
              >
                <div className="g4ai-platform-card-header" onClick={() => toggleCard(i)}>
                  <img src={card.icon} alt="" className="g4ai-platform-card-icon" />
                  <div className="g4ai-platform-card-text">
                    <h3>{card.title}</h3>
                  </div>
                  <span className="g4ai-card-arrow" aria-hidden="true" />
                </div>
                <p className="g4ai-platform-card-detail">{card.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ENTERPRISE DATA PLATFORM */}
      <section className="g4ai-enterprise">
        <img src={enterpriseBg} alt="" className="g4ai-enterprise-bg" />
        <img src={enterpriseMobBg} alt="" className="g4ai-enterprise-mob-bg" />
        <div className="g4ai-enterprise-content">
          <div className="g4ai-enterprise-intro">
            <h2 className="g4ai-section-title">
              Introducing the <span className="g4ai-red">Enterprise</span><br />
              <span className="g4ai-red">Data Platform</span>
            </h2>
            <p>
              Our Enterprise Data Platform is a cloud-native, Kubernetes-orchestrated
              infrastructure that unifies data ingestion, transformation, governance,
              AI integration, and operational monitoring into a single control plane.
            </p>
          </div>
          <div className="g4ai-features-grid">
            {featureCards.map((card, i) => (
              <div className="g4ai-feature-card g4ai-animate" key={i}>
                <img src={card.icon} alt="" className="g4ai-feature-icon" />
                <div className="g4ai-feature-card-text">
                  <h4>{card.title}</h4>
                  <p className="g4ai-feature-subtitle">{card.subtitle}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="g4ai-cta">
        <p className="g4ai-cta-text">
          Reliable AI starts with reliable data. Let&rsquo;s build yours.
        </p>
        <a href="mailto:office@artifit.rs" className="g4ai-btn-secondary">
          Get in touch
        </a>
      </section>

    </div>
  );
};

export default Gravity4AI;
